import React, { useState, useCallback, useEffect } from "react";
import { Tooltip } from "@mui/material";
import { Text } from "@fragments";
import {
    AccountBalanceRounded,
    VpnKeyRounded,
    EmailRounded,
    PhoneRounded,
    BadgeRounded
} from "@mui/icons-material";

import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";
import banks from "@helpers/banks.js";

import Table from "@components/Table";
import StatusChip from "@fragments/StatusChip";

import "./styles.scss";

export default function WithdrawalsTable() {
    // Estados de dados e loading centralizados
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados de paginação
    const [pagination, setPagination] = useState({
        pages: 0,
        records: 0,
        page: 1
    });

    const rowsSize = 15;

    const fetchData = useCallback(async (pageNumber = pagination.page) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            // Adicionar parâmetros de paginação
            params.append('page', pageNumber.toString());
            params.append('per_page', rowsSize);

            const { data } = await api.get(`wallet/withdrawal/list?${params.toString()}`);

            setWithdrawals(data.data || []);

            // Atualizar dados de paginação
            setPagination({
                pages: data.last_page || 0,
                records: data.total || 0,
                page: data.current_page || 1
            });

        } catch (error) {
            console.error('Error fetching withdrawals:', error);
            setWithdrawals([]);
            setPagination({
                pages: 0,
                records: 0,
                page: 1
            });
        } finally {
            setLoading(false);
        }
    }, [pagination.page]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchData(1);
    }, []);

    const handlePageChange = useCallback((newPage) => {
        fetchData(newPage);
    }, [fetchData]);

    const getStatusComponent = (status) => {
        const StatusComponent = {
            'success': StatusChip.Success,
            'failed': StatusChip.Error,
            'default': StatusChip.Default,
        }[status || 'default'];

        const statusLabels = {
            'success': 'Pago',
            'failed': 'Falhou',
        };

        return <StatusComponent text={statusLabels[status] || 'Desconhecido'} />;
    };

    const rowsConfig = [
        {
            title: 'Valor do saque',
            customRender: (data) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Text.h5 className="bold" style={{ width: '100px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                        R$ {Parser.price.brl(data.value?.amount)}
                    </Text.h5>
                    <Text.span>BRL</Text.span>
                    <Tooltip title={data.status === 'failed' ? (data.result?.fail_reason || 'Falha desconhecida') : ''} arrow>
                        <div>
                            {getStatusComponent(data.status)}
                        </div>
                    </Tooltip>
                </div>
            )
        },
        {
            title: 'Destino',
            customRender: (data) => {
                const withdrawal = data.wallet?.withdrawal || {};
                const receiver = withdrawal.receiver || {};
                const bankCode = receiver.bank?.code;
                const bank = banks.find(b => parseInt(b.code) === parseInt(bankCode));
                const owner = receiver.owner || {};

                // Key formatting
                const keyType = withdrawal.keyType;
                const keyValue = withdrawal.key;
                let keyFormatted = keyValue;
                let KeyIcon = VpnKeyRounded;

                if (keyType === 'email') {
                    keyFormatted = Parser.email(keyValue);
                    KeyIcon = EmailRounded;
                } else if (keyType === 'phone') {
                    keyFormatted = Parser.phone('+55' + keyValue);
                    KeyIcon = PhoneRounded;
                } else if (keyType === 'cpf') {
                    keyFormatted = Parser.cpf(keyValue);
                    KeyIcon = BadgeRounded;
                } else if (keyType === 'cnpj') {
                    keyFormatted = Parser.cnpj(keyValue);
                    KeyIcon = BadgeRounded;
                } else if (keyType === 'evp') {
                    keyFormatted = keyValue;
                    KeyIcon = VpnKeyRounded;
                }

                return (
                    <div className="condensed-banking-info" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden' }}>
                        <div className="bank-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            {bank?.logo ? (
                                <img src={bank.logo} alt={bank.name} style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '50%' }} />
                            ) : (
                                <AccountBalanceRounded style={{ fontSize: '20px' }} />
                            )}
                            <Text.h5 className="bank-name text-uppercase" weight={400}>{bank?.nick || bank?.name || 'Banco desconhecido'}</Text.h5>
                        </div>

                        <span className="bullet" style={{ flexShrink: 0 }}>•</span>

                        <div className="key-row" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                            <KeyIcon className="key-icon" />
                            <Text.span className="key-value" contrast={5} style={{ whiteSpace: 'nowrap' }}>
                                {keyFormatted || '-'}
                            </Text.span>
                        </div>

                        <span className="bullet" style={{ flexShrink: 0 }}>•</span>

                        <div className="owner-row" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                            <Text.span className="owner-name" contrast={5}>{owner.name || '-'}</Text.span>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Data da transferência',
            render: (data) => {
                if (!data.timestamps?.created) return '-';
                const date = new Date(data.timestamps.created);
                return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
            }
        }
    ];

    return (
        <div className="withdrawals-table mt-3 mb-6">
            <Text.h3 weight={500}>
                Histórico de saques
            </Text.h3>
            <Table
                key={loading}
                data={withdrawals}
                loading={loading}
                loadingRows={4}
                emptyMessage="Nenhum saque encontrado"
                layout="banking-withdrawals-layout"
                rowsConfig={rowsConfig}
                enablePagination={true}
                pages={pagination.pages}
                records={pagination.records}
                page={pagination.page}
                onSelectPage={handlePageChange}
            />
        </div>
    )
}
